import { useState } from 'react';
import Logo from '../../../components/kit/logo/Logo';
import CloseActionButton from '../../../components/kit/action-button/CloseActionButton';
import Text from '../../../components/kit/text/Text';
import styles from './PrivacyNotification.module.css';
import Button from '../../../components/kit/button/Button';
import Link from '../../../components/kit/link/Link';
import StyledCheckbox from '../../../components/kit/checkbox/Checkbox';

type PrivacyNotificationProps = {
  isVisible: boolean;
  onClose: () => void;
  onContinue: () => void;
};

const PrivacyNotification = ({
  isVisible,
  onClose,
  onContinue,
}: PrivacyNotificationProps) => {
  const [isConfirmed, setIsConfirmed] = useState(false);

  return (
    <div className={isVisible ? styles.wrapper : styles.hidden}>
      <div className={styles.header}>
        <CloseActionButton onClose={onClose} />
        <div className={styles.logo}>
          <Logo type='primary' style={{ width: '93px' }} />
        </div>
      </div>
      <div className={styles.content}>
        <div id='privacy-notification'>
          <Text weight='bold' size='m-large' tag='h2'>
            Privacy notification for the use of the My Vydura Buddy module
          </Text>
          <Text size='small'>Last updated: June 2024</Text>
          <Text size='medium' weight='bold' tag='h3'>
            Introduction
          </Text>
          <Text size='small'>
            The My Vydura Buddy module is offered on the basis of a commercial
            collaboration between Aptar and Pfizer as a digital patient support
            platform for individuals taking Vydura medication. My Vydura Buddy
            module is, under no circumstances, meant to make a diagnosis or
            provide actual medical treatment for migraines or any other related
            health condition. For a proper diagnosis of your medical condition
            and the related treatment, we urge you to consult a healthcare
            professional.
          </Text>
          <Text size='small'>
            When using the My Vydura Buddy module, H. Pfizer A/S and its
            affiliates (collectively, <b>“Pfizer”</b> or <b>“we”</b>) will (as
            joint controllers) collect, process, use and disclose personal data
            about you as detailed in this Privacy Notification.
          </Text>
          <Text size='small'>
            Pfizer will process your personal data collected in the My Vydura
            Buddy module according to applicable legislation, including the EU
            General Data Protection Regulation (<b>“GDPR”</b>).
          </Text>
          <Text size='small'>
            If you decide to use the Migraine Buddy App independently from the
            My Vydura Buddy module, you decide to do so on the terms and
            conditions set out by Aptar (available{' '}
            <Link link='' disabled>
              here
            </Link>
            ).
          </Text>
          <Text size='small' weight='bold'>
            Pfizer is not responsible for nor involved in any way in the
            processing activity related to the use of Migraine Buddy.
          </Text>
          <Text size='medium' weight='bold' tag='h3'>
            What categories of personal data are we processing, for what purpose
            and on which legal basis?
          </Text>
          <Text size='small'>
            No Personal Data is collected in the My Vydura Buddy module. Only
            deidentified, aggregated will be processed and there will be
            absolutely no way to trace any of this information to your personal
            identifiable information.
          </Text>
          <Text size='m-small' weight='bold' tag='h4'>
            Categories of data collected
          </Text>
          <Text size='small'>
            When using the My Vydura Buddy module, you will be asked to provide
            the following details about yourself. 
          </Text>
          <Text size='small' tag='div'>
            <ul>
              <li>Your age;</li>
              <li>Your gender;</li>
              <li>Your location;</li>
              <li>Your migraine history;</li>
              <li>Information about your Vydura treatment.</li>
            </ul>
          </Text>
          <Text size='m-small' weight='bold' tag='h4'>
            Purpose
          </Text>
          <Text size='small'>
            The purpose for our collection and processing of your personal data
            through your use of the My Vydura Buddy module is to improve 
            digital support functionality in your treatment journey.
          </Text>
          <Text size='m-small' weight='bold' tag='h4'>
            Legal basis
          </Text>
          <Text size='small'>
            Your consent, cf. GDPR Article. 6(1)(a), as provided when
            downloading and using the My Vydura Buddy module.
          </Text>
          <Text size='medium' weight='bold' tag='h3'>
            How did we obtain your personal data?
          </Text>
          <Text size='small'>
            No Personal Data is collected in the My Vydura Buddy module. Only
            deidentified, aggregated will be processed and there will be
            absolutely no way to trace any of this information to your personal
            identifiable information.
          </Text>
          <Text size='medium' weight='bold' tag='h3'>
            Will we disclose your personal data to third parties?
          </Text>
          <Text size='small'>
            No Personal Data is collected in the My Vydura Buddy module. Only
            deidentified, aggregated will be processed and there will be
            absolutely no way to trace any of this information to your personal
            identifiable information.
          </Text>
          <Text size='medium' weight='bold' tag='h3'>
            Will we disclose your personal data to data processors?
          </Text>
          <Text size='small'>
            No Personal Data is collected in the My Vydura Buddy module. Only
            deidentified, aggregated will be processed and there will be
            absolutely no way to trace any of this information to your personal
            identifiable information.
          </Text>
          <Text size='small'>
            Pfizer will not receive any personal data from Aptar Pte. Ltd. as we
            have instructed Aptar Pte. Ltd. to ensure full anonymization of your
            personal data prior to submitting such information to Pfizer.
            Consequently, we will only receive aggregated and fully anonymized
            data in relation to the usage of the My Vydura Buddy module and will
            not be able to identify any of the users.
          </Text>
          <Text size='medium' weight='bold' tag='h3'>
            Will we transfer your personal data to recipients in countries
            outside the EU/EEA?
          </Text>
          <Text size='small'>
            No Personal Data is collected in the My Vydura Buddy module. Only
            deidentified, aggregated will be processed and there will be
            absolutely no way to trace any of this information to your personal
            identifiable information.
          </Text>
          <Text size='small'>
            We will not transfer your personal data to any recipients in
            countries outside the EU/EEA. Instead, we may share aggregated and
            fully anonymized data in relation to the usage of the My Vydura
            Buddy module within the Pfizer Group and with partners (where
            relevant).
          </Text>
          <Text size='medium' weight='bold' tag='h3'>
            How long will we store your personal data?
          </Text>
          <Text size='small'>
            No Personal Data is collected in the My Vydura Buddy module. Only
            deidentified, aggregated will be processed and there will be
            absolutely no way to trace any of this information to your personal
            identifiable information.
          </Text>
          <Text size='medium' weight='bold' tag='h3'>
            Your rights
          </Text>
          <Text size='small'>
            We are committed to safeguarding your personal data and your related
            rights. As we are not able to identify you at any point in time, we
            refer you to Aptar Pte. Ltd. in relation to exercising your rights.
            Please note that certain limitations may apply to your ability to
            exercise these rights.
          </Text>
          <Text size='small'>
            Subject to these limitations, you have the following rights:
          </Text>
          <ul>
            <li>
              <Text size='m-small' weight='bold'>
                Right of access
              </Text>
              <Text size='small'>
                You have the right to request access to the personal data being
                processed about you.
              </Text>
            </li>
            <li>
              <Text size='m-small' weight='bold'>
                Right to rectification
              </Text>
              <Text size='small'>
                You have the right to rectification of inaccurate personal data
                concerning you, including completion of incomplete personal
                data. This is done by yourself when using the My Vydura Buddy
                module.
              </Text>
            </li>
            <li>
              <Text size='m-small' weight='bold'>
                Right to erasure (right to be forgotten)
              </Text>
              <Text size='small'>
                You have the right to the erasure of the personal data
                concerning you. You can delete your account directly in the My
                Vydura Buddy module.
              </Text>
            </li>
            <li>
              <Text size='m-small' weight='bold'>
                Right to restriction
              </Text>
              <Text size='small'>
                You have the right to restrict Pfizer's processing of personal
                data concerning you.
              </Text>
            </li>
            <li>
              <Text size='m-small' weight='bold'>
                Right to data portability
              </Text>
              <Text size='small'>
                Where processing is based on your consent, you have the right to
                receive the personal data concerning you in a structured,
                commonly used and machine-readable format. You have the right to
                transmit this personal data to a third party without hindrance
                from Pfizer or Aptar Pte. Ltd., if technically possible.
              </Text>
            </li>
          </ul>
          <Text size='small'>
            As the processing of your personal data is based on your explicit
            consent, you may withdraw your consent at any time. Please note that
            this does not affect Pfizer's processing of your personal data prior
            to the withdrawal of your consent.
          </Text>
          <Text size='small'>
            You also have the right to lodge a complaint with the competent
            supervisory authority, such as the{' '}
            <Link link='' disabled>
              Italian Data Protection Agency
            </Link>
            .
          </Text>
          <Text size='medium' weight='bold' tag='h3'>
            Contact details of Pfizer and Pfizer’s Group Data Protection Officer
          </Text>
          <Text size='small'>
            Should you have any questions in general in regards to the
            protection of your personal data, then please contact Pfizer or
            Pfizer’s Group Data Protection Officer by using the below contact
            details. We will then facilitate a dialogue with Aptar Pte. Ltd. as
            applicable.
          </Text>
          <Text size='m-small' tag='h4' weight='bold'>
            Pfizer Inc. Headquarters
          </Text>
          <Text size='small'>
            66 Hudson Blvd E, Fl 20
            <br />
            New York, NY 10001, US
            <br />
            <Link link='' disabled>
              MapQuest
            </Link>
          </Text>
          <Text size='m-small' tag='h4' weight='bold'>
            Pfizer customer service
          </Text>
          <Text size='small'>
            <b>Phone:</b> 1-800-879-3477
            <br />
            <b>Email:</b>{' '}
            <Link link='mailto:support@pfizer.com'>support@pfizer.com</Link>
          </Text>
          <Text size='m-small' tag='h4' weight='bold'>
            Pfizer’s Group Data Protection Officer
          </Text>
          <Text size='small'>
            <b>Email:</b>{' '}
            <Link link='mailto:dataprivacy@pfizer.com'>support@pfizer.com</Link>
            <br />
            <b>Letter:</b> Use above address, att: Data Protection Officer
            <br />
            <b>Phone:</b> Use above phone number and ask to speak to the Data
            Protection Officer
          </Text>
          <Text size='small'>
            If you contact us via the above email address, H. Pfizer A/S, and
            other Pfizer group entities (as applicable), will process the
            personal data you provide to us with in order to handle and respond
            to your inquiry. Please read our privacy notice regarding this
            processing activity{' '}
            <Link link='' disabled>
              here
            </Link>
            .
          </Text>
          <Text size='medium' weight='bold' tag='h3'>
            Data protection consent
          </Text>
          <StyledCheckbox
            checked={isConfirmed}
            onCheck={() => setIsConfirmed((prev) => !prev)}
            label='I confirm that I have read and understood the Vydura Buddy Privacy Notification to my personal data being processed as described therein.'
          />
        </div>
        <div className={styles.footer}>
          <Button
            type='primary'
            width='full-width'
            isReadOnly={!isConfirmed}
            onClick={onContinue}
          >
            I agree and want to continue
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyNotification;
